import React, { useEffect, useMemo, useState } from 'react';
import { execApi } from '@/utils/api';
import { Badge } from '@/app/components/ui/badge';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/app/components/ui/input-otp';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/app/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/app/components/ui/tabs';
import { toast } from 'sonner';

function formatMoney(value?: number | null) {
  if (value == null) {
    return '—';
  }
  return `$${value.toLocaleString()}`;
}

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatDate(value?: string | null) {
  if (!value) {
    return '—';
  }
  return new Date(value).toLocaleDateString('fr-FR');
}

export default function ExecPortal() {
  const [dashboard, setDashboard] = useState<any>(null);
  const [devisList, setDevisList] = useState<any[]>([]);
  const [contractsList, setContractsList] = useState<any[]>([]);
  const [invoicesList, setInvoicesList] = useState<any[]>([]);
  const [accounting, setAccounting] = useState<any>(null);
  const [signatures, setSignatures] = useState<any[]>([]);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [devisForm, setDevisForm] = useState({
    company_id: '',
    offer_type: 'REGIE',
    geo_zone: 'FR',
    scope_text: '',
    pack_level: 'L1',
    duration_months: '3',
  });
  const [invoiceForm, setInvoiceForm] = useState({
    mission_id: '',
    company_id: '',
    period: currentPeriod(),
  });
  const [sigDialog, setSigDialog] = useState<any>(null);
  const [otpValue, setOtpValue] = useState('');
  const [activeTab, setActiveTab] = useState('devis');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [dashboardRes, devisRes, contractsRes, invoicesRes, accountingRes, signaturesRes, snapshotsRes] = await Promise.all([
        execApi.dashboard(),
        execApi.devis(),
        execApi.contracts(),
        execApi.invoices(),
        execApi.accounting().catch(() => null),
        execApi.signatures().catch(() => ({ signatures: [] })),
        execApi.snapshots().catch(() => ({ snapshots: [] })),
      ]);
      setDashboard(dashboardRes);
      setDevisList(devisRes.devis || []);
      setContractsList(contractsRes.contracts || []);
      setInvoicesList(invoicesRes.invoices || []);
      setAccounting(accountingRes);
      setSignatures(signaturesRes.signatures || []);
      setSnapshots(snapshotsRes.snapshots || []);
    } catch {
      toast.error('Failed to load executive room');
    } finally {
      setLoading(false);
    }
  }

  async function createDevis() {
    try {
      const result = await execApi.createDevis({
        ...devisForm,
        duration_months: parseInt(devisForm.duration_months, 10),
      });
      toast.success(`Devis ${result.number} created: ${formatMoney(result.amount_ttc)}`);
      const devisRes = await execApi.devis();
      setDevisList(devisRes.devis || []);
    } catch {
      toast.error('Failed to create devis');
    }
  }

  async function createContract(devisId: string) {
    try {
      const result = await execApi.createContract(devisId);
      toast.success(`Contract ${result.number} created`);
      const [contractsRes, dashboardRes] = await Promise.all([execApi.contracts(), execApi.dashboard()]);
      setContractsList(contractsRes.contracts || []);
      setDashboard(dashboardRes);
    } catch (error: any) {
      const message = error?.body?.detail || 'Failed to create contract';
      toast.error(message);
    }
  }

  async function createInvoice() {
    try {
      const result = await execApi.createInvoice(invoiceForm);
      toast.success(`Invoice ${result.number} issued`);
      const [invoicesRes, dashboardRes] = await Promise.all([execApi.invoices(), execApi.dashboard()]);
      setInvoicesList(invoicesRes.invoices || []);
      setDashboard(dashboardRes);
    } catch {
      toast.error('Failed to issue invoice');
    }
  }

  async function requestSignature(docType: string, docId: string) {
    try {
      const result = await execApi.requestSignature({
        document_type: docType,
        document_id: docId,
        signer_name: 'Authorized Signer',
        signer_email: 'signer@company.tld',
      });
      setSigDialog({ ...result, document_type: docType });
      toast.success('OTP sent to signer email');
    } catch {
      toast.error('Failed to request signature');
    }
  }

  async function completeSignature() {
    if (!sigDialog || otpValue.length < 6) {
      return;
    }

    try {
      await execApi.completeSignature(sigDialog.signature_id, otpValue);
      toast.success('Document signed successfully');
      setSigDialog(null);
      setOtpValue('');
      await loadData();
    } catch {
      toast.error('Invalid OTP or signature expired');
    }
  }

  async function markPaid(invoiceId: string) {
    try {
      const result = await execApi.markPaid(invoiceId);
      toast.success(`Invoice paid: ${result.payment_ref}`);
      const [invoicesRes, dashboardRes] = await Promise.all([execApi.invoices(), execApi.dashboard()]);
      setInvoicesList(invoicesRes.invoices || []);
      setDashboard(dashboardRes);
    } catch {
      toast.error('Failed to mark invoice as paid');
    }
  }

  const snapshot = dashboard?.snapshot;
  const latestSnapshot = snapshots[0] || null;
  const pendingQuotes = useMemo(() => {
    return devisList.filter((devis) => ['draft', 'pending'].includes((devis.status || '').toLowerCase())).length;
  }, [devisList]);
  const readyForContract = useMemo(() => {
    return devisList.filter((devis) => (devis.status || '').toLowerCase() === 'signed').length;
  }, [devisList]);
  const unpaidInvoices = useMemo(() => {
    return invoicesList.filter((invoice) => (invoice.status || '').toLowerCase() !== 'paid');
  }, [invoicesList]);
  const overdueInvoices = useMemo(() => {
    const today = new Date();
    return unpaidInvoices.filter((invoice) => {
      if (!invoice.due_date) {
        return false;
      }
      return new Date(invoice.due_date) < today;
    }).length;
  }, [unpaidInvoices]);
  const pendingSignatures = useMemo(() => {
    return signatures.filter((signature) => (signature.status || '').toLowerCase() !== 'completed');
  }, [signatures]);
  const contractStats = useMemo(() => {
    return {
      active: contractsList.filter((contract) => contract.status === 'active').length,
      signed: contractsList.filter((contract) => contract.status === 'signed').length,
      pendingSignature: contractsList.filter((contract) => contract.status !== 'signed').length,
    };
  }, [contractsList]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505] text-indigo-300">
        <div className="animate-pulse text-sm font-mono uppercase tracking-[0.3em]">Loading Executive Room</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Geist',sans-serif] antialiased">
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-white/[0.05] bg-[#050505]/90 px-8 py-4 backdrop-blur-xl">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-indigo-400/60">B15 · B19 · Finance Core</div>
          <div className="text-lg font-semibold text-white">Executive Room</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="border-emerald-500/20 bg-emerald-500/10 text-emerald-300">{dashboard?.active_contracts || 0} active contracts</Badge>
          <Badge className="border-white/10 bg-white/5 text-zinc-300">{dashboard?.active_missions || 0} live missions</Badge>
        </div>
      </div>

      <div className="mx-auto max-w-7xl space-y-6 px-8 py-8">
        <div className="grid gap-4 md:grid-cols-5">
          <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">MRR</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-emerald-400">{formatMoney(snapshot?.mrr)}</p></CardContent></Card>
          <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">ARR</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-cyan-300">{formatMoney(snapshot?.arr)}</p></CardContent></Card>
          <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Gross Margin</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-amber-300">{snapshot?.gross_margin_pct || 0}%</p></CardContent></Card>
          <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Collected</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-emerald-400">{formatMoney(dashboard?.invoices?.collected)}</p></CardContent></Card>
          <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Outstanding</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-rose-300">{formatMoney(dashboard?.invoices?.pending)}</p></CardContent></Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {(dashboard?.packs || []).map((pack: any) => (
            <Card key={pack.pack} className="border-white/10 bg-[linear-gradient(135deg,rgba(24,24,32,0.96),rgba(10,10,14,0.96))]">
              <CardHeader className="pb-3"><CardTitle className="text-xs uppercase tracking-[0.3em] text-zinc-500">Pack {pack.pack}</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold text-indigo-300">{pack.clients || 0}</div>
                <div className="mt-1 text-sm text-zinc-500">active clients</div>
                <div className="mt-4 text-lg font-semibold text-emerald-400">{formatMoney(pack.mrr)}/mo</div>
              </CardContent>
            </Card>
          ))}
          <Card className="border-white/10 bg-[linear-gradient(135deg,rgba(24,24,32,0.96),rgba(10,10,14,0.96))]">
            <CardHeader className="pb-3"><CardTitle className="text-xs uppercase tracking-[0.3em] text-zinc-500">Latest Snapshot</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-zinc-400">
              <div className="flex items-center justify-between"><span>DSO</span><span className="font-mono text-white">{latestSnapshot?.dso || snapshot?.dso || 0}</span></div>
              <div className="flex items-center justify-between"><span>Pipeline Value</span><span className="font-mono text-white">{formatMoney(latestSnapshot?.pipeline_value || snapshot?.pipeline_value)}</span></div>
              <div className="flex items-center justify-between"><span>Date</span><span className="font-mono text-white">{latestSnapshot?.snapshot_date || '—'}</span></div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.25fr,1fr]">
          <Card className="border-white/10 bg-[linear-gradient(135deg,rgba(24,24,32,0.96),rgba(10,10,14,0.96))]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Executive command deck</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Commercial lane</div>
                <div className="mt-3 text-2xl font-semibold text-white">{pendingQuotes}</div>
                <div className="mt-1 text-sm text-zinc-400">quotes pending approval or signature</div>
                <div className="mt-3 text-xs text-emerald-300">{readyForContract} ready for contract conversion</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Cash discipline</div>
                <div className="mt-3 text-2xl font-semibold text-white">{unpaidInvoices.length}</div>
                <div className="mt-1 text-sm text-zinc-400">open invoices awaiting settlement</div>
                <div className="mt-3 text-xs text-rose-300">{overdueInvoices} overdue beyond due date</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Governance</div>
                <div className="mt-3 text-2xl font-semibold text-white">{pendingSignatures.length}</div>
                <div className="mt-1 text-sm text-zinc-400">signatures still waiting for completion</div>
                <div className="mt-3 text-xs text-cyan-300">ledger {accounting?.balance?.balanced ? 'balanced' : 'to reconcile'}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-[linear-gradient(135deg,rgba(24,24,32,0.96),rgba(10,10,14,0.96))]">
            <CardHeader>
              <CardTitle className="text-lg text-white">Action lanes</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setActiveTab('devis')} className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10">Review quotes</Button>
                <Button variant="outline" onClick={() => setActiveTab('contracts')} className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10">Drive contracts</Button>
                <Button variant="outline" onClick={() => setActiveTab('invoices')} className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10">Close invoicing</Button>
                <Button variant="outline" onClick={() => setActiveTab('signatures')} className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10">Chase signatures</Button>
                <Button variant="outline" onClick={() => setActiveTab('fec')} className="border-white/10 bg-white/[0.03] text-white hover:bg-white/10">Audit ledger</Button>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
                Default entry now centralises quote, contract, invoice, signature, and FEC status in one command surface instead of dropping directly into a single subpage.
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Next contract actions</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-zinc-300">
              {contractsList.slice(0, 3).map((contract: any) => (
                <div key={contract.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                  <div>
                    <div className="font-mono text-xs text-white">{contract.number}</div>
                    <div className="text-xs text-zinc-500">{contract.company_name || contract.company_id || '—'}</div>
                  </div>
                  <Badge className="border-white/10 bg-white/5 text-zinc-300">{contract.status}</Badge>
                </div>
              ))}
              {contractsList.length === 0 && <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-4 text-zinc-500">No contracts in queue.</div>}
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Cash watch</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-zinc-300">
              {unpaidInvoices.slice(0, 3).map((invoice: any) => (
                <div key={invoice.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                  <div>
                    <div className="font-mono text-xs text-white">{invoice.number}</div>
                    <div className="text-xs text-zinc-500">due {formatDate(invoice.due_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-white">{formatMoney(invoice.amount_ttc)}</div>
                    <div className="text-xs text-zinc-500">{invoice.status}</div>
                  </div>
                </div>
              ))}
              {unpaidInvoices.length === 0 && <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-4 text-zinc-500">All visible invoices are settled.</div>}
            </CardContent>
          </Card>
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Audit and signature</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm text-zinc-300">
              <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                <div className="text-xs text-zinc-500">Latest snapshot</div>
                <div className="mt-1 text-white">{formatDate(latestSnapshot?.snapshot_date)}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                <div className="text-xs text-zinc-500">Pending signatures</div>
                <div className="mt-1 text-white">{pendingSignatures.length}</div>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 px-3 py-2">
                <div className="text-xs text-zinc-500">Ledger state</div>
                <div className="mt-1 text-white">{accounting?.balance?.balanced ? 'Balanced' : 'Unbalanced'}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="h-auto flex-wrap gap-1 rounded-xl border border-white/[0.06] bg-white/[0.03] p-1">
            <TabsTrigger value="devis">Devis</TabsTrigger>
            <TabsTrigger value="contracts">Contracts</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="signatures">E-Signature</TabsTrigger>
            <TabsTrigger value="fec">FEC / Snapshots</TabsTrigger>
          </TabsList>

          <TabsContent value="devis" className="space-y-4">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader>
                <CardTitle className="text-lg text-white">Generate Devis</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-6">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Company ID</Label>
                  <Input value={devisForm.company_id} onChange={(event) => setDevisForm((state) => ({ ...state, company_id: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Pack</Label>
                  <Select value={devisForm.pack_level} onValueChange={(value) => setDevisForm((state) => ({ ...state, pack_level: value }))}>
                    <SelectTrigger className="mt-1.5 border-white/10 bg-white/[0.03] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="L1">L1</SelectItem>
                      <SelectItem value="L2">L2</SelectItem>
                      <SelectItem value="L3">L3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Geo</Label>
                  <Select value={devisForm.geo_zone} onValueChange={(value) => setDevisForm((state) => ({ ...state, geo_zone: value }))}>
                    <SelectTrigger className="mt-1.5 border-white/10 bg-white/[0.03] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FR">France</SelectItem>
                      <SelectItem value="UK">United Kingdom</SelectItem>
                      <SelectItem value="CH">Switzerland</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="US">USA</SelectItem>
                      <SelectItem value="AE">UAE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Offer</Label>
                  <Select value={devisForm.offer_type} onValueChange={(value) => setDevisForm((state) => ({ ...state, offer_type: value }))}>
                    <SelectTrigger className="mt-1.5 border-white/10 bg-white/[0.03] text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="REGIE">Régie</SelectItem>
                      <SelectItem value="FORFAIT">Forfait</SelectItem>
                      <SelectItem value="URGENT">Urgence</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Duration</Label>
                  <Input type="number" value={devisForm.duration_months} onChange={(event) => setDevisForm((state) => ({ ...state, duration_months: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
                <div className="flex items-end">
                  <Button onClick={createDevis} className="w-full bg-white text-black hover:bg-zinc-100">Generate</Button>
                </div>
                <div className="md:col-span-6">
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Scope</Label>
                  <Input value={devisForm.scope_text} onChange={(event) => setDevisForm((state) => ({ ...state, scope_text: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader><CardTitle className="text-lg text-white">Quote-to-Contract Queue</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-500">Devis #</TableHead>
                      <TableHead className="text-zinc-500">Company</TableHead>
                      <TableHead className="text-zinc-500">Type</TableHead>
                      <TableHead className="text-zinc-500">TTC</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devisList.map((devis: any) => (
                      <TableRow key={devis.id} className="border-white/5">
                        <TableCell className="font-mono text-white">{devis.number}</TableCell>
                        <TableCell className="text-zinc-300">{devis.company_name || devis.company_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-zinc-400">{devis.offer_type}</TableCell>
                        <TableCell className="font-semibold text-white">{formatMoney(devis.amount_ttc)}</TableCell>
                        <TableCell><Badge className="border-white/10 bg-white/5 text-zinc-300">{devis.status}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {devis.status === 'draft' && (
                              <Button size="sm" variant="outline" onClick={() => requestSignature('devis', devis.id)} className="border-violet-500/30 text-violet-300">
                                Sign
                              </Button>
                            )}
                            {devis.status === 'signed' && (
                              <Button size="sm" onClick={() => createContract(devis.id)} className="bg-emerald-600 hover:bg-emerald-500">
                                Create Contract
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {devisList.length === 0 && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={6} className="py-10 text-center text-zinc-500">No quotes visible yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contracts" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Active</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-emerald-400">{contractStats.active}</p></CardContent></Card>
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Signed</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-cyan-300">{contractStats.signed}</p></CardContent></Card>
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Pending Signature</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-amber-300">{contractStats.pendingSignature}</p></CardContent></Card>
            </div>
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-500">Contract #</TableHead>
                      <TableHead className="text-zinc-500">Company</TableHead>
                      <TableHead className="text-zinc-500">Type</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Start</TableHead>
                      <TableHead className="text-zinc-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contractsList.map((contract: any) => (
                      <TableRow key={contract.id} className="border-white/5">
                        <TableCell className="font-mono text-white">{contract.number}</TableCell>
                        <TableCell className="text-zinc-300">{contract.company_name}</TableCell>
                        <TableCell className="text-zinc-400">{contract.offer_type}</TableCell>
                        <TableCell><Badge className="border-white/10 bg-white/5 text-zinc-300">{contract.status}</Badge></TableCell>
                        <TableCell className="text-zinc-400 font-mono text-xs">{contract.start_date}</TableCell>
                        <TableCell>
                          {contract.status !== 'signed' && (
                            <Button size="sm" variant="outline" onClick={() => requestSignature('contract', contract.id)} className="border-violet-500/30 text-violet-300">
                              Request Signature
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {contractsList.length === 0 && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={6} className="py-10 text-center text-zinc-500">No contracts visible yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="invoices" className="space-y-4">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader><CardTitle className="text-lg text-white">Issue Invoice</CardTitle></CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Company ID</Label>
                  <Input value={invoiceForm.company_id} onChange={(event) => setInvoiceForm((state) => ({ ...state, company_id: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Mission ID</Label>
                  <Input value={invoiceForm.mission_id} onChange={(event) => setInvoiceForm((state) => ({ ...state, mission_id: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Period</Label>
                  <Input value={invoiceForm.period} onChange={(event) => setInvoiceForm((state) => ({ ...state, period: event.target.value }))} className="mt-1.5 border-white/10 bg-white/[0.03] text-white" />
                </div>
                <div className="flex items-end">
                  <Button onClick={createInvoice} className="w-full bg-white text-black hover:bg-zinc-100">Issue</Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-500">Invoice #</TableHead>
                      <TableHead className="text-zinc-500">Company</TableHead>
                      <TableHead className="text-zinc-500">HT</TableHead>
                      <TableHead className="text-zinc-500">TTC</TableHead>
                      <TableHead className="text-zinc-500">Due</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicesList.map((invoice: any) => (
                      <TableRow key={invoice.id} className="border-white/5">
                        <TableCell className="font-mono text-white">{invoice.number}</TableCell>
                        <TableCell className="text-zinc-300">{invoice.company_name}</TableCell>
                        <TableCell className="text-zinc-300">{formatMoney(invoice.amount_ht)}</TableCell>
                        <TableCell className="font-semibold text-white">{formatMoney(invoice.amount_ttc)}</TableCell>
                        <TableCell className="text-zinc-400 font-mono text-xs">{invoice.due_date}</TableCell>
                        <TableCell><Badge className="border-white/10 bg-white/5 text-zinc-300">{invoice.status}</Badge></TableCell>
                        <TableCell>
                          {invoice.status !== 'paid' && (
                            <Button size="sm" onClick={() => markPaid(invoice.id)} className="bg-emerald-600 hover:bg-emerald-500">
                              Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {invoicesList.length === 0 && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={7} className="py-10 text-center text-zinc-500">No invoices visible yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="signatures">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader>
                <CardTitle className="text-lg text-white">Sovereign E-Signature Center</CardTitle>
                <p className="text-sm text-zinc-500">OTP + SHA256 chain, no external signature provider dependency.</p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-500">Document</TableHead>
                      <TableHead className="text-zinc-500">Signer</TableHead>
                      <TableHead className="text-zinc-500">Status</TableHead>
                      <TableHead className="text-zinc-500">Requested</TableHead>
                      <TableHead className="text-zinc-500">Certificate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signatures.map((signature: any) => (
                      <TableRow key={signature.id} className="border-white/5">
                        <TableCell className="text-white"><span className="mr-2 font-mono text-xs text-zinc-500">{signature.document_type}</span>{signature.document_id?.slice(0, 8)}</TableCell>
                        <TableCell className="text-zinc-300">{signature.signer_name}</TableCell>
                        <TableCell><Badge className="border-white/10 bg-white/5 text-zinc-300">{signature.status}</Badge></TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400">{signature.requested_at?.slice(0, 16)}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-500">{signature.certificate_hash?.slice(0, 16)}…</TableCell>
                      </TableRow>
                    ))}
                    {signatures.length === 0 && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={5} className="py-10 text-center text-zinc-500">No signature workflow initiated yet.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fec" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Total Debit</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-emerald-400">{formatMoney(accounting?.balance?.total_debit)}</p></CardContent></Card>
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Total Credit</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-rose-300">{formatMoney(accounting?.balance?.total_credit)}</p></CardContent></Card>
              <Card className="border-white/10 bg-white/[0.03]"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-zinc-500">Ledger State</CardTitle></CardHeader><CardContent><p className="text-2xl font-semibold text-white">{accounting?.balance?.balanced ? 'Balanced' : 'Unbalanced'}</p></CardContent></Card>
            </div>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader><CardTitle className="text-lg text-white">Financial Snapshots</CardTitle></CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {snapshots.slice(0, 8).map((item: any) => (
                  <div key={item.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">{item.snapshot_date}</div>
                    <div className="mt-3 text-lg font-semibold text-white">{formatMoney(item.mrr)} MRR</div>
                    <div className="mt-1 text-sm text-zinc-400">ARR {formatMoney(item.arr)}</div>
                    <div className="mt-1 text-sm text-zinc-400">Pipeline {formatMoney(item.pipeline_value)}</div>
                  </div>
                ))}
                {snapshots.length === 0 && (
                  <div className="rounded-2xl border border-white/10 bg-black/20 p-6 text-sm text-zinc-500">No financial snapshots available.</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader><CardTitle className="text-lg text-white">FEC Entries</CardTitle></CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-zinc-500">Journal</TableHead>
                      <TableHead className="text-zinc-500">Date</TableHead>
                      <TableHead className="text-zinc-500">Account</TableHead>
                      <TableHead className="text-zinc-500">Label</TableHead>
                      <TableHead className="text-zinc-500 text-right">Debit</TableHead>
                      <TableHead className="text-zinc-500 text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(accounting?.entries || []).slice(0, 50).map((entry: any, index: number) => (
                      <TableRow key={index} className="border-white/5">
                        <TableCell className="font-mono text-xs text-zinc-400">{entry.journal_code}</TableCell>
                        <TableCell className="font-mono text-xs text-zinc-400">{entry.ecriture_date}</TableCell>
                        <TableCell className="font-mono text-white">{entry.compte_num}</TableCell>
                        <TableCell className="text-zinc-300">{entry.ecriture_lib}</TableCell>
                        <TableCell className="text-right font-mono text-emerald-400">{entry.debit > 0 ? formatMoney(entry.debit) : '—'}</TableCell>
                        <TableCell className="text-right font-mono text-rose-300">{entry.credit > 0 ? formatMoney(entry.credit) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {(!accounting?.entries || accounting.entries.length === 0) && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={6} className="py-10 text-center text-zinc-500">No FEC entries available.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {sigDialog && (
        <Dialog open={!!sigDialog} onOpenChange={() => setSigDialog(null)}>
          <DialogContent className="border-white/[0.08] bg-[#0a0a0a] text-white">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">Complete E-Signature</DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">OTP sent to</div>
                <div className="mt-1 font-mono text-sm text-cyan-400">{sigDialog.otp_sent_to}</div>
                <div className="mt-2 break-all font-mono text-[10px] text-zinc-600">{sigDialog.certificate_hash}</div>
              </div>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otpValue} onChange={setOtpValue}>
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button onClick={completeSignature} className="w-full bg-white text-black hover:bg-zinc-100" disabled={otpValue.length < 6}>
                Verify & Sign
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}